import {
  DEFAULT_SEARCH_WEIGHTS,
  MAX_SEARCH_V2_KEYWORDS,
} from "@/lib/constants";
import { SearchV2Filters, SearchV2Weights } from "@/types";

const SEARCH_V2_RECENT_QUERIES_STORAGE_KEY = "searchV2RecentSearchesByJobId";
const MAX_RECENT_SEARCHES = 5;

export interface RecentSearchV2Request {
  filters: SearchV2Filters;
  weights: SearchV2Weights;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every((item: unknown) => typeof item === "string");

const parseFilters = (value: unknown): SearchV2Filters | null => {
  if (!isRecord(value)) return null;

  const { includePastSubmitted, includeRejected, keywords, payCeiling, title } =
    value;
  if (
    !Array.isArray(keywords) ||
    typeof payCeiling !== "string" ||
    typeof includeRejected !== "boolean" ||
    !isStringArray(title)
  ) {
    return null;
  }

  const parsedKeywords: SearchV2Filters["keywords"] = [];
  for (const item of keywords as unknown[]) {
    if (!isRecord(item) || typeof item.keyword !== "string") return null;
    parsedKeywords.push({
      category: (item.category ?? null) as SearchV2Filters["keywords"][number]["category"],
      color: (item.color ?? null) as SearchV2Filters["keywords"][number]["color"],
      keyword: item.keyword,
    });
  }

  return {
    includePastSubmitted: includePastSubmitted === true,
    includeRejected,
    // Writes are capped upstream; re-cap on restore so a tampered or legacy
    // entry can't push an oversized keyword list into the search request.
    keywords: parsedKeywords.slice(0, MAX_SEARCH_V2_KEYWORDS),
    payCeiling,
    title,
  };
};

const parseWeights = (value: unknown): SearchV2Weights | null => {
  if (!isRecord(value)) return null;

  const { activityWeight, semanticWeight, textWeight, weRecencyWeight } =
    value;
  if (
    typeof activityWeight !== "number" ||
    typeof semanticWeight !== "number" ||
    typeof textWeight !== "number"
  ) {
    return null;
  }

  return {
    activityWeight,
    semanticWeight,
    textWeight,
    // Older stored weights predate the WE recency tuner; fall back to default.
    weRecencyWeight:
      typeof weRecencyWeight === "number"
        ? weRecencyWeight
        : DEFAULT_SEARCH_WEIGHTS.weRecencyWeight,
  };
};

const parseRequests = (value: unknown): RecentSearchV2Request[] => {
  if (!Array.isArray(value)) return [];

  const requests: RecentSearchV2Request[] = [];
  for (const item of value as unknown[]) {
    if (!isRecord(item)) continue;
    const filters = parseFilters(item.filters);
    const weights = parseWeights(item.weights);
    if (filters && weights) requests.push({ filters, weights });
  }
  return requests;
};

const getRequestsByJobId = (): Record<string, RecentSearchV2Request[]> => {
  // Storage failures (private mode, quota) degrade to an empty history rather
  // than throwing during render.
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SEARCH_V2_RECENT_QUERIES_STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};

  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!isRecord(parsed)) return {};

  const requestsByJobId: Record<string, RecentSearchV2Request[]> = {};
  for (const [jobId, storedRequests] of Object.entries(parsed)) {
    const requests = parseRequests(storedRequests);
    if (requests.length) requestsByJobId[jobId] = requests;
  }
  return requestsByJobId;
};

// Built from values in a fixed order — stringifying the objects directly would
// make the key depend on property insertion order, which differs between
// requests built from app state and ones re-read from storage.
const requestKey = ({ filters, weights }: RecentSearchV2Request) =>
  JSON.stringify([
    filters.includePastSubmitted,
    filters.includeRejected,
    filters.keywords.map(({ keyword }) => keyword.toLowerCase()).sort(),
    filters.payCeiling,
    [...filters.title].sort(),
    weights.activityWeight,
    weights.semanticWeight,
    weights.textWeight,
    weights.weRecencyWeight,
  ]);

export const searchV2RecentQueriesStorage = {
  // Most recent first, deduped on the full request, capped at 5.
  add: (jobId: string, request: RecentSearchV2Request): RecentSearchV2Request[] => {
    const requestsByJobId = getRequestsByJobId();
    const requests = [
      request,
      ...(requestsByJobId[jobId] ?? []).filter(
        (existingRequest) => requestKey(existingRequest) !== requestKey(request),
      ),
    ].slice(0, MAX_RECENT_SEARCHES);
    requestsByJobId[jobId] = requests;
    try {
      localStorage.setItem(
        SEARCH_V2_RECENT_QUERIES_STORAGE_KEY,
        JSON.stringify(requestsByJobId),
      );
    } catch {
      // Keep the in-memory result; the entry just won't survive a refresh.
    }
    return requests;
  },
  get: (jobId: string): RecentSearchV2Request[] => {
    return getRequestsByJobId()[jobId] ?? [];
  },
};
